import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import Hls from 'hls.js'

import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import {
  EndActions,
  eventTypes,
} from '@sportworld/js-sdk/src/types/servicesTypes/configTypes'
import { isHlsExtension, isMp4Extension } from '@sportworld/js-sdk/src/utils'

import * as Styles from '@/components/InspectPane/styles'
import { VideoPlaybackStatus } from '@/constants'
import { channelSliderImgWidth } from '@/constants/tiles'
import { useActiveBroadcast } from '@/hooks'
import { useSize } from '@/hooks/useSize'
import { useUserInteraction } from '@/hooks/useUserInteraction'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { Keys } from '@/platform/platform'
import { useAdsStore } from '@/store/useAdsStore'
import useStore from '@/store/useStore'
import { VideoPlayerProps } from '@/types'
import {
  findNearestByImgWidth,
  isWebos,
  showError,
  showInfo,
  showSuccess,
  showWarning,
} from '@/utils/utils'
import { addSsaiParams, replaceVideoUrlParams } from '@/utils/videoParamsUtils'

import {
  BlockedOverlayWrapper,
  LogoPreloader,
  Preloader,
  VideoElement,
  VideoError,
  VideoPlayerLabel,
} from './styles'
import { VideoControls } from './VideoControls'
import { Loader } from '../Loader'
import { AdControls } from './AdControls'
import { BlockedOverlay } from './BlockedOverlay'
import { useBackKeyAnalytics, usePreventWebosScreenSaver } from './hooks/hooks'
import { usePlayerAnalytics } from './hooks/useAnalytics'
import { VideoOverlayTypes } from './hooks/useVideoOverlay'
import { VideoEndedOverlay } from './VideoEndedOverlay'
import { ContentUnavailable } from '../ContentUnavailable/ContentUnavailable'

/**
 * USAGE
 * <HlsAdsPlayer
    src="https://b1smarttv-nativewaves-3.amagi.tv/stream7/sw01/data/master.m3u8"
    adTagUrl="https://tv.springserve.com/rt/91980?w=1920&h=1080&cb=44646394&ip=217.119.77.134&ua=Mozilla/5.0%20(Macintosh;%20Intel%20Mac%20OS%20X%2010_15_7)%20AppleWebKit/537.36%20(KHTML,%20like%20Gecko)%20Chrome/137.0.0.0%20Safari/537.36&lat=52.2491&lon=20.978&dnt=0&ic=REPLACE_ME&dur=REPLACE_ME&ap=1&pod_max_dur=30&pod_ad_slots=0-20,0-20&app_bundle=not%20supported&app_name=Sportworld&app_store_url=https://seller.samsungapps.com/tv/tizen-application/distribute/3201903018105&content_id=elf-beyond-the-huddle$ep2&content_title=ELF%20Beyond%20The%20Huddle&content_genre=REPLACE_ME&content_livestream=0&content_categories=REPLACE_ME&channel_name=elf%20live%20g1&content_custom_1_param=pre-roll&content_custom_2_param=b1smarttv&language=de-DE&network_name=Sportworld&did=not%20supported&device_make=apple&device_model=not%20supported&brand_name=apple&country=DE&ifa_type=tifa&gdpr_consent=CQRgQAAQRgQAAAFADBENBrFsAP_gAEPgAAYgJzQJ4AFAAWABAACoAIIATgBRACwALQAZAA0AB4AEKAIwAjgBMACcAFuAMIAxABzAEAAIQARgAygBqgDiAIQAR0AkoBOAC6gI1AWGAvYBf4DGQGWANrAgKBBoCQQEvQJzAnNASAAWABUAEAAMgAaAA8ACOAEwAMQAfgBCADKAIQAR0AkoC_wGWATmAAAA.f_wACHwAAAAA&gdpr=1&us_privacy=REPLACE_ME&coppa=REPLACE_ME&lmt=0"
  />
 */

const HlsAdsPlayer: FC<VideoPlayerProps> = ({
  src,
  isMuted = false,
  isBlocked = false,
  autoplay = false,
  broadcastId = '',
  focusIdPrefix = '',
  currentEvent,
  channelFromProps,
  withControls = true,
  isStageHome = false,
  withUserInteraction,
  onEndedAction = EndActions.LOOP,
  inPagePlayer = false,
  videoData,
  adSettings,
  isStandalone = false,
  title,
  search,
  isRequiresRegistrationOnly,
  videoOverlay,
  scrollToSection,
  setVideoPlaybackStatus,
  setVideoOverlay,
  onExitButton,
}) => {
  const videoElementRef = useRef<HTMLVideoElement>(null)
  const adContainerRef = useRef<HTMLDivElement>(null)
  const adDisplayContainer = useRef<google.ima.AdDisplayContainer | null>(null)
  const adsManager = useRef<google.ima.AdsManager | null>(null)
  const adsLoader = useRef<google.ima.AdsLoader | null>(null)
  const hlsInstance = useRef<Hls | null>(null)
  const videoElementSize = useSize(videoElementRef)
  const videoId = videoData?.id || src || ''

  ;(window as any).video = videoElementRef.current

  const isVideoLoadingRef = useRef(false)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(autoplay)
  const [isVideoStarted, setIsVideoStarted] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isVideoEnded, setIsVideoEnded] = useState(false)

  const isAdLoadingRef = useRef(false)
  const [isAdPlaying, setIsAdPlaying] = useState(false)
  const setAdProgression = useAdsStore((state) => state.setAdProgression)

  const { isFullscreen, hideVideoControls, setIsFullscreen } = useStore(
    (state) => state.player,
  )

  const isFullscreenOrStandalone = isFullscreen || isStandalone

  const { t } = useCommonTranslation()

  const isHls = isHlsExtension(src)
  const isMp4 = isMp4Extension(src)

  const isVod = videoData?.type === eventTypes.VOD
  const isChannel = videoData?.type === eventTypes.CHANNEL
  const isEventType = isChannel || isVod
  const isFreeVideo = isBlocked && src // isBlockedChanneWithFreeVideo
  const isCloseEndAction =
    onEndedAction.toLocaleLowerCase() === EndActions.CLOSE

  const { activeChannel: channelFromBroadcastHook, channels } =
    useActiveBroadcast(broadcastId)

  const activeChannel = channelFromBroadcastHook || channelFromProps

  const isNonWatchableChannel =
    !!activeChannel && !activeChannel.watchable && !isBlocked && !src

  const isEnabledImaSdkDebugState = useStore(
    (state) => state.common.appSettings.isEnabledImaSdkDebug,
  )

  const isEnabledImaSdkDebug = isEnabledImaSdkDebugState && isDevMode

  let adTagUrl = adSettings?.csaiAdUrl || adSettings?.csaiSettings?.preRollAdUrl

  adTagUrl = replaceVideoUrlParams({
    url: adTagUrl,
  })

  useEffect(() => {
    isDevMode && console.log('[HlsAdsPlayer] src changed')

    initialisePlayback()

    return () => {
      isDevMode && console.log('[HlsAdsPlayer] Cleanup src change')
      destroyHls()
      destroyIma()
      destoyHtml5Video()
    }
  }, [src])

  useEffect(() => {
    isDevMode &&
      console.log(
        '[HlsAdsPlayer] isFullscreen changed. isAdLoading: ',
        isAdLoadingRef.current,
        ' | isFullscreen: ',
        isFullscreen,
      )
    //If ads are already being initialised, skip re-initialising
    if (isAdLoadingRef.current && !isAdPlaying) {
      isDevMode && console.log('[HlsAdsPlayer] isAdLoading or Playing. Skip.')
      return
    }
    //If no longer playing in fullscreen, stop ad playback
    if (
      (adTagUrl && isFullscreenOrStandalone) ||
      (!isFullscreenOrStandalone && isAdPlaying)
    ) {
      isDevMode &&
        console.log(
          '[HlsAdsPlayer] isFullscreen changed - Initialise new playback for ad',
        )
      destroyHls()
      destroyIma()
      destoyHtml5Video()
      initialisePlayback()
    }
  }, [isFullscreenOrStandalone])

  useEffect(() => {
    return () => {
      isDevMode && console.log('[HlsAdsPlayer] Cleanup component')
      destroyHls()
      destroyIma()
      destoyHtml5Video()
    }
  }, [])

  useEffect(() => {
    setIsError(false)
    setIsVideoLoaded(false)
    setIsVideoStarted(false)
    setVideoPlaybackStatus?.(null)
    setIsVideoEnded(false)
  }, [src])

  /**
   * The order of initialization is important
   * - IMA
   * - HLS
   */
  function initialisePlayback() {
    if (!src) {
      const msg = '[HlsAdsPlayer] Video source not found'
      isEnabledImaSdkDebug && showInfo(msg)
      isDevMode && console.log(msg)
      return
    } else {
      isDevMode && console.log('[HlsAdsPlayer] src:', src)
    }

    if (!videoElementRef.current) {
      const msg = '[HlsAdsPlayer] Video element not found'
      isEnabledImaSdkDebug && showInfo(msg)
      isDevMode && console.log(msg)
      return
    }

    // Only initialize IMA if player is in fullscreen mode
    isDevMode &&
      console.log('[HlsAdsPlayer] isFullscreen: ', {
        isFullscreen,
        isStandalone,
      })
    if (adTagUrl && isFullscreenOrStandalone) {
      initializeIMA()

      try {
        adDisplayContainer?.current?.initialize()
      } catch (error) {
        const msg = '[HlsAdsPlayer] Error initializing AdDisplayContainer'
        isEnabledImaSdkDebug && showError(msg)
        console.error(msg, error)
      }
    } else {
      const msg = '[HlsAdsPlayer] No AdTagUrl set'
      isEnabledImaSdkDebug && console.log(msg, { adTagUrl, videoElementRef })
      isEnabledImaSdkDebug && showWarning(msg)

      // If no ads, start playback immediately
      startContentPlayback()
    }
  }

  function startContentPlayback() {
    isDevMode &&
      console.log(
        '[HlsAdsPlayer startContentPlayback] Starting content playback',
      )
    isDevMode &&
      showInfo('[HlsAdsPlayer startContentPlayback] Starting content playback')

    if (!src) {
      isDevMode &&
        showError('[HlsAdsPlayer startContentPlayback] Video source not found')
      isDevMode &&
        console.log(
          '[HlsAdsPlayer startContentPlayback] Video source not found',
        )
      return
    }

    if (!videoElementRef.current) {
      isDevMode &&
        showError('[HlsAdsPlayer startContentPlayback] Video element not found')
      isDevMode &&
        console.log(
          '[HlsAdsPlayer startContentPlayback] Video element not found',
        )
      return
    }

    if (videoElementRef.current.currentTime > 0) {
      play()
      isDevMode &&
        console.log(
          '[HlsAdsPlayer startContentPlayback] Video already attached, resuming playback',
        )
      return
    }

    isDevMode &&
      console.log(
        '[HlsAdsPlayer startContentPlayback] isLoading: ',
        isVideoLoadingRef.current,
        ' | isVideoLoaded: ',
        isVideoLoaded,
        '| isVideoPlaying: ',
        isVideoPlaying,
        ' | isVideoStarted: ',
        isVideoStarted,
      )
    if (isVideoLoadingRef.current) {
      isDevMode &&
        console.log(
          '[HlsAdsPlayer startContentPlayback] Video already loading, don´t initialise again',
        )
      return
    }

    isVideoLoadingRef.current = true
    isDevMode &&
      console.log(
        '[HlsAdsPlayer startContentPlayback] onLoadStart: ',
        isVideoLoadingRef.current,
      )

    const srcWithSsaiParams = addSsaiParams(src, adSettings)

    if (isHls) {
      const msg = '[HlsAdsPlayer] HLS'
      isEnabledImaSdkDebug && showInfo(msg)
      isDevMode && console.log(msg)
      const hls = new Hls()
      hlsInstance.current = hls

      hlsInstance.current.loadSource(srcWithSsaiParams)
      hlsInstance.current.attachMedia(videoElementRef.current)
      hlsInstance.current.config.autoStartLoad = false

      hlsInstance.current.on(Hls.Events.MANIFEST_PARSED, () => {
        isDevMode && console.log('[HlsAdsPlayer initHls] MANIFEST_PARSED')
        const msg = '[HlsAdsPlayer initHls] No ads - starting playback'
        isEnabledImaSdkDebug && showInfo(msg)
        isDevMode && console.log(msg)

        if (!hlsInstance.current) {
          isDevMode &&
            showError(
              '[HlsAdsPlayer startContentPlayback] HLS instance not found',
            )
          isDevMode &&
            console.log(
              '[HlsAdsPlayer startContentPlayback] HLS instance not found',
            )
          return
        }

        hlsInstance.current.startLoad()
        play()
      })

      hlsInstance.current.on(Hls.Events.ERROR, (event, data) => {
        try {
          const msg = '[HlsAdsPlayer initHls] HLS Error:' + JSON.stringify(data)
          isEnabledImaSdkDebug && showError(msg)
          isDevMode && console.error(msg)
        } catch (_) {}
      })
    } else if (isMp4) {
      const msg = '[HlsAdsPlayer] MP4'
      isEnabledImaSdkDebug && showInfo(msg)
      isDevMode && console.log(msg)
      videoElementRef.current.src = src
      play()
    } else {
      isDevMode &&
        console.log(
          '[HlsAdsPlayer startContentPlayback] Not supported video format.',
        )
    }
  }

  function initializeIMA() {
    if (!adTagUrl) {
      isEnabledImaSdkDebug &&
        showInfo('[HlsAdsPlayer initializeIMA] No AdTagUrl set')
      isDevMode && console.log('[HlsAdsPlayer initializeIMA] No AdTagUrl set')
      return
    }
    if (!adContainerRef.current) {
      isEnabledImaSdkDebug &&
        showError('[HlsAdsPlayer initializeIMA] Ad container not found')
      isDevMode &&
        console.error('[HlsAdsPlayer initializeIMA] Ad container not found')
      return
    }
    if (!videoElementRef.current) {
      isEnabledImaSdkDebug &&
        showError('[HlsAdsPlayer initializeIMA] Video element not found')
      isDevMode &&
        console.error('[HlsAdsPlayer initializeIMA] Video element not found')
      return
    }
    if (isAdLoadingRef.current) {
      isDevMode &&
        console.log('[HlsAdsPlayer initializeIMA] IMA already initialised')
      return
    }

    isDevMode && console.log('initializeIMA')
    isEnabledImaSdkDebug && showInfo('[HlsAdsPlayer initializeIMA]')
    isAdLoadingRef.current = true

    if (
      !adDisplayContainer.current &&
      typeof google !== 'undefined' &&
      google &&
      google.ima
    ) {
      isDevMode &&
        console.log('[HlsAdsPlayer initializeIMA] Creating AdDisplayContainer')
      isEnabledImaSdkDebug &&
        showInfo('[HlsAdsPlayer initializeIMA] Creating AdDisplayContainer')
      try {
        adDisplayContainer.current = new google.ima.AdDisplayContainer(
          adContainerRef.current,
          videoElementRef.current,
        )
      } catch (error) {
        isEnabledImaSdkDebug &&
          showError(
            '[HlsAdsPlayer initializeIMA] Error creating AdDisplayContainer',
          )
        isDevMode &&
          console.error(
            '[HlsAdsPlayer initializeIMA] Error creating AdDisplayContainer:',
            error,
          )
      }
    }

    if (!adsLoader.current) {
      isDevMode && console.log('[initializeIMA] Creating AdsLoader')
      isEnabledImaSdkDebug &&
        showInfo('[HlsAdsPlayer initializeIMA] Creating AdsLoader')
      if (
        (typeof google !== 'undefined' && !google.ima) ||
        !adDisplayContainer.current
      ) {
        isEnabledImaSdkDebug &&
          showError(
            '[HlsAdsPlayer initializeIMA] IMA SDK can not load adsLoader',
          )
        isDevMode &&
          console.error(
            '[HlsAdsPlayer initializeIMA] IMA SDK can not load adsLoader',
          )

        setIsAdPlaying(false)
        // If ads fail, start content playback
        startContentPlayback()
        if (adsManager.current) {
          destroyIma()
        }

        return
      }
      adsLoader.current = new google.ima.AdsLoader(adDisplayContainer.current)
      adsLoader.current.addEventListener(
        google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        onAdsManagerLoaded,
        false,
      )
      adsLoader.current.addEventListener(
        google.ima.AdErrorEvent.Type.AD_ERROR,
        (adErrorEvent: google.ima.AdErrorEvent) => {
          isDevMode && console.log('[HlsAdsPlayer] IMA ad error')
          const message = adErrorEvent.getError()?.getMessage()
          isEnabledImaSdkDebug &&
            showError(`[HlsAdsPlayer] IMA ad error: ${message}`)
          isDevMode && console.error(adErrorEvent.getError())
          setIsAdPlaying(false)

          // If ads fail, start content playback
          startContentPlayback()
          if (adsManager.current) {
            destroyIma()
          }
        },
        false,
      )
    }

    const adsRequest = new google.ima.AdsRequest()
    adsRequest.adTagUrl = adTagUrl
    adsRequest.contentDuration = 20000
    adsRequest.vastLoadTimeout = 20000

    isDevMode &&
      console.log(
        '[HlsAdsPlayer initializeIMA] adsRequest',
        adsRequest.adTagUrl,
      )
    isEnabledImaSdkDebug && showInfo('[HlsAdsPlayer initializeIMA] adsRequest')
    adsLoader.current.requestAds(adsRequest)
  }

  /**
   * Handles the ad manager loading and sets ad event listeners.
   * @param {google.ima.AdsManagerLoadedEvent} adsManagerLoadedEvent
   */
  function onAdsManagerLoaded(
    adsManagerLoadedEvent: google.ima.AdsManagerLoadedEvent,
  ) {
    if (!videoElementRef.current) {
      const msg = '[HlsAdsPlayer onAdsManagerLoaded] Video element not found'
      isEnabledImaSdkDebug && showError(msg)
      isDevMode && console.error(msg)
      return
    }
    adsManager.current = adsManagerLoadedEvent.getAdsManager(
      videoElementRef.current,
    )
    const msg = '[HlsAdsPlayer] IMA onAdsManagerLoaded'
    isDevMode && console.log(msg)
    isEnabledImaSdkDebug && showInfo(msg)

    const adsRenderingSettings = new google.ima.AdsRenderingSettings()
    adsRenderingSettings.uiElements = []
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true

    adsManager.current.updateAdsRenderingSettings(adsRenderingSettings)

    adsManager.current.addEventListener(
      google.ima.AdEvent.Type.STARTED,
      (e: google.ima.AdEvent) => {
        const duration = e.getAd()?.getDuration()
        const title = e.getAd()?.getTitle()
        const adId = e.getAd()?.getAdId()
        isDevMode &&
          console.log('[HlsAdsPlayer] IMA ad started', {
            duration,
            adId,
            title,
          })
        isEnabledImaSdkDebug &&
          showSuccess(
            `[HlsAdsPlayer] IMA ad started ${duration}s ${adId} ${title}`,
          )
      },
    )

    adsManager.current.addEventListener(
      google.ima.AdEvent.Type.AD_PROGRESS,
      (e: google.ima.AdEvent) => {
        const adData = e.getAdData?.() as google.ima.AdProgressData
        setAdProgression(adData)
        setIsAdPlaying(true)
      },
    )

    adsManager.current.addEventListener(
      google.ima.AdEvent.Type.COMPLETE,
      (e: google.ima.AdEvent) => {
        setAdProgression(null)
        const title = e.getAd()?.getTitle()
        const adId = e.getAd()?.getAdId()
        const msg = '[HlsAdsPlayer] IMA ad complete'
        setIsAdPlaying(false)
        isEnabledImaSdkDebug && showInfo(`${msg} ${adId} ${title}`)
        isDevMode && console.log(msg, { adId, title })
      },
    )

    adsManager.current.addEventListener(
      google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      () => {
        isDevMode && console.log('[HlsAdsPlayer] IMA content pause requested')
        videoElementRef.current?.pause()
      },
    )

    adsManager.current.addEventListener(
      google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      () => {
        isDevMode && console.log('[HlsAdsPlayer] IMA content resume requested')

        // ===> Start content playback when IMA resumes content, and if nothing is playing yet / starting to play
        startContentPlayback()
      },
    )

    adsManager.current.addEventListener(
      google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
      () => {
        const msg = '[HlsAdsPlayer] IMA all ads completed'
        isDevMode && console.log(msg)
        isEnabledImaSdkDebug && showSuccess(msg)
        setIsAdPlaying(false)

        // ===> Start content playback after all ads complete
        startContentPlayback()
      },
    )

    adsManager.current.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      (adErrorEvent: google.ima.AdErrorEvent) => {
        const adError = adErrorEvent.getError()
        const msg = '[HlsAdsPlayer] AdsManager ad error'
        isDevMode && console.log(msg)
        isDevMode && console.error(adError)
        isEnabledImaSdkDebug && showError(msg)
        isEnabledImaSdkDebug && showError(JSON.stringify(adError).toString())
        setIsAdPlaying(false)

        // ===> Start content playback if ads fail
        startContentPlayback()
      },
    )

    const width = videoElementRef.current?.clientWidth
    const height = videoElementRef.current?.clientHeight

    try {
      adsManager.current.init(width, height)
      adsManager.current.start()
    } catch (adError) {
      const msg = '[HlsAdsPlayer] AdsManager could not be started'
      isDevMode && console.error(msg, adError)
      isEnabledImaSdkDebug && showError(msg)

      // Start content playback if ads manager fails
      startContentPlayback()
    }
  }

  function destoyHtml5Video() {
    if (!videoElementRef.current) {
      const msg =
        '[HlsAdsPlayer] HTML5 video element not found, nothing to destroy'
      isEnabledImaSdkDebug && showInfo(msg)
      isDevMode && console.log(msg)
      return
    }
    const msg = '[HlsAdsPlayer] Destroy HTML5 video'
    isEnabledImaSdkDebug && showInfo(msg)
    isDevMode && console.log(msg)

    isVideoLoadingRef.current = false
    setIsVideoPlaying(false)

    videoElementRef.current.src = ''
  }

  function destroyHls() {
    if (!hlsInstance.current) {
      const msg = '[HlsAdsPlayer] HLS instance not found, nothing to destroy'
      isEnabledImaSdkDebug && showInfo(msg)
      isDevMode && console.log(msg)
      return
    }
    const msg = '[HlsAdsPlayer] Destroy HLS'
    isEnabledImaSdkDebug && showInfo(msg)
    isDevMode && console.log(msg)

    isVideoLoadingRef.current = false
    setIsVideoPlaying(false)

    hlsInstance.current.detachMedia()
    hlsInstance.current.destroy()
    hlsInstance.current = null
  }

  function destroyIma() {
    const msg = '[HlsAdsPlayer] Destroy IMA'
    isDevMode && console.log(msg)
    isEnabledImaSdkDebug && showInfo(msg)

    isAdLoadingRef.current = false
    setAdProgression(null)
    setIsAdPlaying(false)

    adsManager.current?.destroy()
    adsManager.current = null

    adsLoader.current?.destroy()
    adsLoader.current = null

    adDisplayContainer.current?.destroy()
    adDisplayContainer.current = null
  }

  /**
   * HTMLVideoELement native play method.
   *
   * If the video element is available, start playing the video content.
   * If the video element is not available, do not do anything.
   */
  function play() {
    if (videoElementRef.current) {
      videoElementRef.current.play().then(() => {
        isEnabledImaSdkDebug &&
          showSuccess('[HlsAdsPlayer] Content playback started')
        isDevMode && console.log('[HlsAdsPlayer] Content playback started')
        setIsVideoPlaying(true)
      })
    }
  }

  const imgUrl = useMemo(
    () =>
      findNearestByImgWidth(
        activeChannel?.image?.sources,
        channelSliderImgWidth,
        { prioritizeSvg: true },
      )?.url,
    [activeChannel?.id],
  )

  const premiumLockedscreenImage = useMemo(
    () =>
      findNearestByImgWidth(
        activeChannel?.premiumLockedscreenImage?.sources,
        1920,
      )?.url,
    [activeChannel?.id],
  )

  const {
    sendFullScreenAnalyticsEvent,
    sendBackPressAnalyticsEvent,
    sendVideoLoadingAnalyticsEvent,
    sendVideoErrorAnalyticsEvent,
  } = usePlayerAnalytics(
    videoId,
    isVideoStarted,
    isVideoPlaying,
    videoElementRef,
    videoData,
    src,
    activeChannel?.id,
    isMuted,
    isBlocked || isNonWatchableChannel,
    withUserInteraction,
  )

  usePreventWebosScreenSaver({
    isStageHome,
    inPagePlayer,
    isVideoPlaying,
    isFullscreen,
    scrollToSection,
    setIsFullscreen,
    sendFullScreenAnalyticsEvent,
  })

  useBackKeyAnalytics(inPagePlayer, sendBackPressAnalyticsEvent)

  const onUserIdle = useCallback(() => {
    scrollToSection?.()
    setIsFullscreen(true)
    sendFullScreenAnalyticsEvent()
  }, [sendFullScreenAnalyticsEvent])

  const isWebosPlatform = isWebos()

  const detectUserInteraction =
    isWebosPlatform &&
    !isStageHome &&
    inPagePlayer &&
    isVideoPlaying &&
    !isFullscreen

  useUserInteraction(onUserIdle, detectUserInteraction)

  const keyPressHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode == Keys.BACKSPACE || e.keyCode == Keys.BACK) {
        e.preventDefault()
        sendBackPressAnalyticsEvent()
      }
    },
    [sendBackPressAnalyticsEvent],
  )

  useEffect(() => {
    if (!inPagePlayer) {
      return
    }
    window.addEventListener('keyup', keyPressHandler)
    return () => {
      window.removeEventListener('keyup', keyPressHandler)
    }
  }, [inPagePlayer, keyPressHandler])

  /**
   *
   * Native HTMLVideoElement props and events
   *
   */
  const muted = isBlocked || isNonWatchableChannel || isMuted
  const loop = !isAdPlaying && onEndedAction.toLowerCase() === EndActions.LOOP
  const onWaiting = () => {
    isDevMode && console.log('[VideoElement] onWaiting')
    isVideoLoadingRef.current = true
  }
  const onCanPlay = () => {
    isDevMode && console.log('[VideoElement] onCanPlay')
    setIsVideoLoaded(true)
    isVideoLoadingRef.current = false
  }
  const onEnded = () => {
    isDevMode && console.log('[VideoElement] onEnded')
    if (isAdPlaying) {
      isDevMode &&
        console.log('[VideoElement] skip onEnded. Reason: ad is playing')
      return
    }
    isVideoLoadingRef.current = false
    setIsVideoStarted(false)
    setIsVideoPlaying(false)
    setIsVideoEnded(true)
    setVideoPlaybackStatus?.(VideoPlaybackStatus.ENDED)
    if (isCloseEndAction) {
      hideVideoControls()
      if (isEventType) {
        !isFreeVideo &&
          setVideoOverlay?.({ overlayType: VideoOverlayTypes.ENDED })
      } else {
        onExitButton?.()
      }
    }
  }
  const onPlaying = () => {
    isDevMode && console.log('[VideoElement] onPlaying')
    setIsVideoStarted(true)
  }
  const onError = (e: SyntheticEvent<HTMLVideoElement, Event>) => {
    isDevMode && console.log('[VideoElement] onError')
    src && sendVideoErrorAnalyticsEvent(e.type)

    isVideoLoadingRef.current = false
    setIsVideoStarted(false)
    setIsVideoPlaying(false)
  }

  const onLoadStart = () => {
    isDevMode && console.log('[VideoElement] onLoadStart')
    isVideoLoadingRef.current = true
    isDevMode &&
      console.log('[VideoElement] onLoadStart: ', isVideoLoadingRef.current)
    src && !isMuted && !isAdPlaying && sendVideoLoadingAnalyticsEvent()
  }
  /**
   * ================================================================
   */

  const showPreloader =
    !isVideoLoaded && !isError && !isNonWatchableChannel && !isAdPlaying

  const showPreloaderChannelLogo = isChannel && imgUrl && !isVideoLoaded

  const showVideoControls =
    withControls && !isBlocked && !videoOverlay && !isAdPlaying && !isVideoEnded

  const showAdControls = withControls && !isBlocked && isAdPlaying

  const showBlockedOverlay = isBlocked || videoOverlay

  const showChannels = Number(channels?.length) > 1 && Boolean(activeChannel)

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        aspectRatio: '16 / 9',
      }}
    >
      {isDevMode && (
        <VideoPlayerLabel $bgcolor="lightgreen">HlsAdsPlayer</VideoPlayerLabel>
      )}
      <VideoElement
        ref={videoElementRef}
        autoPlay
        muted={muted}
        loop={loop}
        onWaiting={onWaiting}
        onCanPlay={onCanPlay}
        onEnded={onEnded}
        onPlaying={onPlaying}
        onError={onError}
        onLoadStart={onLoadStart}
      />
      <div
        ref={adContainerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      />
      {showPreloader && (
        <Preloader>
          {showPreloaderChannelLogo ? (
            <LogoPreloader
              $src={imgUrl}
              $isBlocked={isBlocked && !src && !isStageHome}
            >
              <img src={imgUrl} alt="" />
            </LogoPreloader>
          ) : (
            !isBlocked && <Loader />
          )}
        </Preloader>
      )}
      {showBlockedOverlay && (
        <BlockedOverlayWrapper
          $hasFreeVideo={!!isFreeVideo}
          $isStageHome={isStageHome}
        >
          {renderBlockedOverlay()}
        </BlockedOverlayWrapper>
      )}
      {(isError || isNonWatchableChannel) && (
        <VideoError>
          <ContentUnavailable>{t('streamUnavailable')}</ContentUnavailable>
        </VideoError>
      )}
      {showVideoControls && (
        <VideoControls
          domRect={videoElementSize}
          videoElementRef={videoElementRef}
          isFullscreen={isFullscreen}
          videoData={videoData}
          showChannels={showChannels}
          setIsVideoPlaying={setIsVideoPlaying}
          isVideoPlaying={isVideoPlaying}
          onExitButton={onExitButton}
          title={title}
          broadcastId={broadcastId}
          currentEvent={currentEvent}
          sliderData={channels || []}
          focusIdPrefix={'focusIdPrefix'}
          isVideoStarted={isVideoStarted}
          channelFromProps={undefined}
        />
      )}
      {showAdControls && (
        <AdControls
          data-component="ad-controls"
          domRect={videoElementSize}
          videoElementRef={videoElementRef}
          isFullscreen={isFullscreen}
          videoData={videoData}
          setIsVideoPlaying={setIsVideoPlaying}
          isVideoPlaying={isVideoPlaying}
          onExitButton={onExitButton}
          currentEvent={currentEvent}
          focusIdPrefix={focusIdPrefix}
          isVideoStarted={isVideoStarted}
        />
      )}
      {isEnabledImaSdkDebug && src && adTagUrl && (
        <Styles.InspectPane
          style={{
            position: 'absolute',
            top: '50%',
            right: '50%',
            width: '1600px',
            transform: 'translate(50%, -50%)',
            backgroundColor: 'rgba(25, 37, 50, 0.7)',
          }}
        >
          <Styles.SectionContent>
            <header>CSAI</header>
            <div style={{ columns: 2, width: '100%' }}>
              {
                <p>
                  <span>url:</span> {new URL(adTagUrl).origin}
                </p>
              }
              {Object.entries(
                Object.fromEntries(
                  new URL(
                    replaceVideoUrlParams({
                      url: adTagUrl,
                      isMuted: false,
                      isAutoplay: true,
                      isFullscreen: true,
                      isVod,
                    }),
                  ).searchParams,
                ),
              ).map(([key, value]) => (
                <p key={key + value}>
                  <span>{key}:</span> {value}
                </p>
              ))}
            </div>
          </Styles.SectionContent>
        </Styles.InspectPane>
      )}
    </div>
  )

  function renderBlockedOverlay() {
    if (videoOverlay?.overlayType === VideoOverlayTypes.BLOCKED) {
      return (
        <BlockedOverlay
          data-component="blocked-overlay"
          domRect={videoElementSize}
          isFullscreen={isFullscreen || isStandalone}
          focusIdPrefix={focusIdPrefix}
          premiumLockedscreenImage={premiumLockedscreenImage}
          showBlockedBgImage={!src || isVideoEnded}
          disabled={isStageHome}
          search={search}
          isRequiresRegistrationOnly={isRequiresRegistrationOnly}
        />
      )
    }
    const isVideoEndedOverlay =
      videoOverlay?.overlayType === VideoOverlayTypes.ENDED ||
      videoOverlay?.overlayType === VideoOverlayTypes.COUNTDOWN

    if (isVideoEndedOverlay && setVideoOverlay) {
      return (
        <VideoEndedOverlay
          focusIdPrefix={focusIdPrefix}
          onExitButton={onExitButton}
          isChannelEventType={isChannel}
          overlayData={videoOverlay}
          setVideoOverlay={setVideoOverlay}
          isFullscreen={isFullscreen || isStandalone}
          isStageHome={isStageHome}
        />
      )
    }
  }
}

export default HlsAdsPlayer
